import request from '@/scripts/request';
import type { MyAxiosResponseData } from '@/scripts/request/types';
import { formatUser } from '@/store/client';
import { userStorage, userSessionStorage, userStorage_client } from '../../storages';
import type {
  signResponse,
  User,
  UserCardResponse,
  UserGroupRelationResponse,
  UserProfileParams,
  UserProfileResponse,
  UserLoginResponse,
  UserInfoResponse,
  UserLoginCodeResponse,
  UserLoginWaitCodeResponse,
  CheckUserSubResponse,
  uploadDeviceParams,
  RefreshGoogleResponse,
} from './types';
import { StorageType } from './types';

export interface LoginParams {
  code: string;
  scope: string;
  state: string;
  redirect_uri: string;
}

export const userApis = {
  postFCMToken(params: Partial<uploadDeviceParams>) {
    return request('/api/v1/user/info/upload-token', {
      method: 'POST',
      data: params,
      disableErrorEvent: true,
    }).then(({ data }) => {
      return data;
    });
  },
  signData(params: MyRecord) {
    return request('/api/v1/user/sign/data', {
      method: 'POST',
      data: params,
      disableErrorEvent: true,
      disableErrorMsg: true,
    }).then(({ data }: { data: signResponse }) => {
      return data;
    });
  },
  login(params: LoginParams) {
    return request
      .AUTH<MyAxiosResponseData<UserLoginResponse>>('/v1/oauth/token/by/code', {
        method: 'POST',
        data: params,
        disableErrorEvent: true,
      })
      .then(async ({ data }) => {
        return data;
      });
  },
  checkUserSub(params: { account_type: string; sub_id: string; token: string }) {
    return request<MyAxiosResponseData<CheckUserSubResponse>>('/api/v1/user/login/check/sub', {
      method: 'POST',
      data: params,
    }).then(({ data }) => data);
  },
  refreshGoogle(params: { refresh_token: string }) {
    return request<MyAxiosResponseData<RefreshGoogleResponse>>(
      '/api/v1/user/login/google/refresh',
      {
        method: 'POST',
        data: params,
      }
    ).then(({ data }) => data);
  },
  getQRCode() {
    return request<MyAxiosResponseData<UserLoginCodeResponse>>('/api/v1/user/login/login-code', {
      method: 'GET',
    });
  },
  waitQRCode(params: {
    code?: string;
    device_id?: string;
    device_type?: number;
    push_token?: string;
    ios_voip_token?: string;
  }) {
    return request<MyAxiosResponseData<UserLoginWaitCodeResponse>>(
      '/api/v1/user/login/login-code/wait',
      {
        method: 'POST',
        data: params,
      }
    ).then(({ data }) => data);
  },
  getState(params: { state: string }) {
    return request
      .AUTH('/v1/oauth/state', {
        method: 'GET',
        params,
      })
      .then(({ data }) => data);
  },
  setTokens(
    { token, id_token }: { token: string; id_token?: string },
    isSessionStorage: boolean = false
  ) {
    userStorage.setItem(
      'tokenStorageType',
      isSessionStorage ? StorageType.SESSION : StorageType.LOCAL
    );

    if (isSessionStorage) {
      userSessionStorage.setItem('token', token);
      userSessionStorage.setItem('id_token', id_token);
    } else {
      userStorage.setItem('token', token);
      userStorage.setItem('id_token', id_token);
    }
  },
  getToken() {
    const storageType = userStorage.getItem('tokenStorageType');
    if (storageType === StorageType.SESSION) {
      return userSessionStorage.getItem('token');
    } else {
      return userStorage.getItem('token');
    }
  },
  getIdToken() {
    const storageType = userStorage.getItem('tokenStorageType');
    if (storageType === StorageType.SESSION) {
      return userSessionStorage.getItem('id_token');
    } else {
      return userStorage.getItem('id_token');
    }
  },
  removeToken() {
    userStorage.removeItem('tokenStorageType');
    userSessionStorage.removeItem('token');
    userStorage.removeItem('token');
    userStorage.removeItem('id_token');
  },
  setTokenOld(token: string) {
    return userStorage.setItem('token_old', token);
  },
  getTokenOld() {
    return userStorage.getItem('token_old');
  },
  removeTokenOld() {
    return userStorage.removeItem('token_old');
  },
  isLogin() {
    return Boolean(this.getToken() && this.getUserId());
  },
  setUserId(user_id: string) {
    userStorage.setItem('id', user_id);
  },
  getUserId() {
    return userStorage.getItem('id');
  },
  removeUserId() {
    return userStorage.removeItem('id');
  },
  setIsNewUser(state: boolean) {
    userSessionStorage.setItem('isNewUser', state);
  },
  getIsNewUser() {
    return userSessionStorage.getItem('isNewUser');
  },
  removeIsNewUser() {
    return userSessionStorage.removeItem('isNewUser');
  },
  setClientUid(clientUid: string) {
    userStorage.setItem('clientUid', clientUid);
  },
  getClientUid() {
    return userStorage.getItem('clientUid');
  },
  removeClientUid() {
    return userStorage.removeItem('clientUid');
  },
  setIcloudAccess(val: boolean) {
    userStorage.setItem('icloudAccess', val);
  },
  getIcloudAccess() {
    return userStorage.getItem('icloudAccess');
  },
  setCloudInfo(val: any) {
    userStorage.setItem('cloudInfo', val);
  },
  getCloudInfo() {
    return userStorage.getItem('cloudInfo');
  },
  removeCloudInfo() {
    return userStorage.removeItem('cloudInfo');
  },
  setAuthInfo(val: any) {
    userStorage.setItem('authInfo', val);
  },
  getAuthInfo() {
    return userStorage.getItem('authInfo');
  },
  removeAuthInfo() {
    return userStorage.removeItem('authInfo');
  },
  clearAppleCloudData() {
    const deleteCookie = (name: string) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    };

    localStorage.removeItem(import.meta.env.VITE_CLOUD_KIT_CONTAINER_IDENTIFIER);
    localStorage.removeItem('id_token');
    deleteCookie(import.meta.env.VITE_CLOUD_KIT_CONTAINER_IDENTIFIER);
    deleteCookie('X-APPLE-WEBAUTH-AC-PARTITION');
    deleteCookie('X-APPLE-WEBAUTH-AC-SERVERINFO');
    deleteCookie('X-APPLE-WEBAUTH-AC-TOKEN');
  },
  userInfoUpdate(params: {
    name?: string;
    first_name?: string;
    last_name?: string;
    avatar?: string;
  }) {
    return request('/api/v1/user/info/update', {
      method: 'POST',
      data: params,
    });
  },
  usernameCheck(params: { username: string }) {
    return request<MyAxiosResponseData<{ code: number; message: string }>>(
      '/api/v1/user/username/check',
      {
        method: 'POST',
        data: params,
        disableErrorMsg: true,
        disableErrorEvent: true,
      }
    );
  },
  usernameUpdate(params: { username: string }) {
    return request('/api/v1/user/username/update', {
      method: 'PUT',
      data: params,
    });
  },
  updateUserProfile(params: UserProfileParams) {
    return request('/api/v1/user/profile', {
      method: 'POST',
      data: params,
    });
  },
  getUserProfile(uid: string) {
    return request<MyAxiosResponseData<UserProfileResponse>>('/api/v1/user/profile', {
      params: {
        uid,
      },
    });
  },
  async setUser(user: User) {
    const userInfo: User = {
      access: '*',
      ...user,
    };
    await userStorage_client.setItem('user', userInfo);
    return userInfo;
  },
  async getUserLocal() {
    return await userStorage_client.getItem('user');
  },
  async getUser() {
    // let user = await userStorage_client.getItem('user');
    // if (!user) {
    const res = await this.getUserApi();
    const user = await this.setUser(res as any);
    // }
    return user as User;
  },
  removeUserLocal() {
    return userStorage_client.removeItem('user');
  },
  async getUserCard(uid: string) {
    return await request<MyAxiosResponseData<UserCardResponse>>('/api/v1/user/card', {
      method: 'GET',
      params: {
        uid,
      },
    });
  },
  async getUserGroupRelation(uid: string) {
    return await request<MyAxiosResponseData<UserGroupRelationResponse>>(
      '/api/v1/group/user/relation',
      {
        method: 'GET',
        params: {
          uid,
        },
      }
    );
  },
  async getUserApi() {
    const res = await request<MyAxiosResponseData<UserInfoResponse>>('/api/v1/user/info').then(
      ({ data }) => {
        return formatUser(data as any);
      }
    );
    return res;
  },
  async removeUserStorage() {
    const token = this.getToken();
    this.setTokenOld(token);

    this.removeToken();
    this.removeUserId();
    this.removeClientUid();
    this.clearAppleCloudData();
    this.removeAuthInfo();
    this.removeCloudInfo();
    this.removeAuthParams();
    await this.removeUserLocal();
    // TODO amo: clear client state and SDK state
  },
  search(keyword = '') {
    const abortController = new AbortController();

    const p = keyword
      ? request('/api/v1/user/search', {
          signal: abortController.signal,
          params: {
            keyword,
          },
        }).then((res) =>
          res.data.list.map((item: User) => {
            formatUser(item as any);
            return Object.assign(item, {
              label: item.name,
              value: item.user_id,
            });
          })
        )
      : Promise.resolve([]);

    return Object.assign(p, { abortController });
  },
  getAuthParams() {
    return userStorage.getItem('authParams');
  },
  setAuthParams(val: any) {
    userStorage.setItem('authParams', val);
  },
  updateAuthParams(val: any) {
    const oldVal = userStorage.getItem('authParams');
    userStorage.setItem('authParams', { ...oldVal, ...val });
  },
  removeAuthParams() {
    return userStorage.removeItem('authParams');
  },
};
